import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Alert,
  Card,
  Stack,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  People as PeopleIcon,
  Inventory2 as InventoryIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import DashboardChartCard from '../common/DashboardChartCard';
import DashboardMetricCard from '../common/DashboardMetricCard';
import DashboardReportHeader from '../common/DashboardReportHeader';
import { getOrganizationStatistics } from '../../services/api';
import {
  computeOccupancyMetrics,
  getHotelTotalRooms,
  splitBookingsWithinCapacity,
} from '../../utils/performanceMetrics';
import { resolveDashboardThemeMode } from '../../utils/dashboardSurfaceSx';
import {
  getInsightsChartPrimaryGridProps,
  getInsightsChartSecondaryGridProps,
  getInsightsGridSpacing,
  getInsightsMetricGridProps,
} from '../../utils/insightsDashboardLayout';
import { detectThemeSync, watchThemeChanges } from '../../utils/themeUtils';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';

function getThemeColors(isDark) {
  if (isDark) {
    return {
      booking: '#4ce1b6',
      revenue: '#ff6b7d',
      expense: '#f8e082',
      hotelDue: '#8ac9ff',
      profit: '#66d4a8',
      inventory: '#8ac9ff',
      directBooked: '#66d4a8',
      onlineBooked: '#f8e082',
      overbooked: '#ff4861',
      customer: '#f6da6e',
      cancelled: '#ff4861',
      active: '#66d4a8',
    };
  }
  return {
    booking: '#4ce1b6',
    revenue: '#ff4861',
    expense: '#f6da6e',
    hotelDue: '#70bbfd',
    profit: '#4ce1b6',
    inventory: '#70bbfd',
    directBooked: '#4ce1b6',
    onlineBooked: '#f6da6e',
    overbooked: '#ff4861',
    customer: '#f6da6e',
    cancelled: '#ff4861',
    active: '#4ce1b6',
  };
}

const ROOMNEXA_LOGO_URL = '/static/topbar/logo_ed_dark.png';

const loadRoomnexaLogoDataUrl = async () => {
  try {
    const response = await fetch(ROOMNEXA_LOGO_URL);
    if (!response.ok) return null;
    const logoBlob = await response.blob();
    const objectUrl = URL.createObjectURL(logoBlob);
    try {
      const image = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = objectUrl;
      });
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth || 300;
      canvas.height = image.naturalHeight || 87;
      const context = canvas.getContext('2d');
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/png');
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } catch (error) {
    return null;
  }
};

/**
 * Same metrics and charts as landing org dashboard (?view=dashboard), scoped to the
 * currently selected hotel via getOrganizationStatistics([hotel.id], ...).
 */
export default function HotelPerformanceDashboard({ hotel }) {
  const muiTheme = useTheme();
  const reportPageOneRef = useRef(null);
  const reportPageTwoRef = useRef(null);
  const exportInProgressRef = useRef(false);
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isDark = resolveDashboardThemeMode(currentTheme) === 'dark';
  const themeColors = useMemo(() => getThemeColors(isDark), [isDark]);
  const unifiedChartColors = useMemo(
    () => (isDark
      ? {
          red: '#ff6b7d',
          green: '#4ce1b6',
          yellow: '#f8e082',
        }
      : {
          red: '#ff4861',
          green: '#4ce1b6',
          yellow: '#f6da6e',
        }),
    [isDark]
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [startDate, setStartDate] = useState(() => moment().startOf('month'));
  const [endDate, setEndDate] = useState(() => moment().endOf('month'));
  const [pdfExporting, setPdfExporting] = useState(false);

  useEffect(() => {
    const cleanup = watchThemeChanges((next) => setCurrentTheme(next));
    return cleanup;
  }, []);

  const handleDateRangeChange = useCallback((nextStartDate, nextEndDate) => {
    if (nextStartDate?.isValid?.() && nextEndDate?.isValid?.()) {
      setStartDate(nextStartDate);
      setEndDate(nextEndDate);
    }
  }, []);

  const fetchData = useCallback(async () => {
    if (!hotel?.id || !startDate?.isValid() || !endDate?.isValid()) return;
    if (startDate.isAfter(endDate)) {
      setError({
        title: 'Invalid date range',
        message: 'Start date must be on or before end date.',
      });
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await getOrganizationStatistics(
        [hotel.id],
        startDate.format('YYYY-MM-DD'),
        endDate.format('YYYY-MM-DD')
      );
      setStatistics(data.statistics || null);
    } catch (err) {
      const responseData = err.responseData || err.response?.data || {};
      let message = err.userMessage || responseData.error || responseData.message || responseData.detail || 'Failed to load statistics';
      setError({ title: 'Unable to load performance', message });
      setStatistics(null);
    } finally {
      setLoading(false);
    }
  }, [hotel?.id, startDate, endDate]);

  useEffect(() => {
    if (!hotel?.id) {
      setStatistics(null);
      setError(null);
      return;
    }
    fetchData();
  }, [hotel?.id, fetchData]);

  const chartData = useMemo(() => {
    const sourceStatistics = statistics || {};
    const dailyStats = sourceStatistics.daily_stats || [];
    const dailyStatsByDate = dailyStats.reduce((acc, stat) => {
      if (stat?.date) {
        acc[stat.date] = stat;
      }
      return acc;
    }, {});

    const fullRangeStats = [];
    const currentDate = startDate.clone().startOf('day');
    const lastDate = endDate.clone().startOf('day');

    while (currentDate.isSameOrBefore(lastDate)) {
      const dateKey = currentDate.format('YYYY-MM-DD');
      const stat = dailyStatsByDate[dateKey] || {};

      fullRangeStats.push({
        ...stat,
        date: dateKey,
        name: currentDate.format('MMM DD'),
      });

      currentDate.add(1, 'day');
    }

    return {
      bookingStatsData: fullRangeStats.map((stat) => ({
        name: stat.name,
        bookings: stat.bookings || 0,
        active: stat.active_bookings || 0,
        cancelled: stat.cancelled_bookings || 0,
        booking: Math.max(
          (stat.bookings || 0) -
            (stat.active_bookings || 0) -
            (stat.cancelled_bookings || 0),
          0
        ),
      })),
      financeStatsData: fullRangeStats.map((stat) => ({
        name: stat.name,
        revenue: stat.revenue || 0,
        expense: stat.expense || 0,
        hotelDue: stat.hotel_due || 0,
        profit: stat.profit || 0,
      })),
      inventoryStatsData: fullRangeStats.map((stat) => {
        const hotelTotalRooms = getHotelTotalRooms(sourceStatistics);
        const dayTotal = Number(stat.total_hotel_rooms ?? stat.total ?? hotelTotalRooms ?? 0);
        const rawDirectBooked = stat.direct_booked || 0;
        const rawOnlineBooked = stat.online_booked || 0;
        const bookingSplit = splitBookingsWithinCapacity(rawDirectBooked, rawOnlineBooked, dayTotal);
        const booked = stat.rooms_booked ?? stat.booked_rooms ?? (rawDirectBooked + rawOnlineBooked);
        return {
          name: stat.name,
          total: dayTotal,
          direct_booked: bookingSplit.direct_booked,
          online_booked: bookingSplit.online_booked,
          overbooked: bookingSplit.overbooked,
          booked,
          available: dayTotal > 0 ? Math.max(dayTotal - Number(booked || 0), 0) : 0,
          rooms_booked: booked,
        };
      }),
    };
  }, [statistics, startDate, endDate]);

  const bookingStatsData = chartData?.bookingStatsData || [];
  const financeStatsData = chartData?.financeStatsData || [];
  const inventoryStatsData = chartData?.inventoryStatsData || [];
  const bookingSourcePalette = [
    '#4ce1b6',
    '#f6da6e',
    '#70bbfd',
    '#ff4861',
    '#8e7dff',
    '#ff9f43',
    '#26c6da',
    '#66bb6a',
  ];
  const bookingSourceColorMap = {
    Direct: '#4ce1b6',
    Reservation: '#f6da6e',
    'Booking.com': '#2563eb',
    Agoda: '#8e24aa',
    MakeMyTrip: '#ef4444',
    MMT: '#ef4444',
    WalkIn: '#70bbfd',
    'Walk-In': '#70bbfd',
  };
  const bookingSourceChartData = (statistics?.booking_source_breakdown || [])
    .map((entry, idx) => {
      const name = String(entry?.source || entry?.name || entry?.channel || '').trim();
      return {
        name,
        value: Number(entry?.value) || 0,
        color: bookingSourceColorMap[name] || bookingSourcePalette[idx % bookingSourcePalette.length],
      };
    })
    .filter((item) => item.name && item.value > 0)
    .sort((a, b) => b.value - a.value);
  const totalSourceBookings = bookingSourceChartData.reduce((sum, item) => sum + Number(item.value || 0), 0);
  const paymentMethodPalette = [
    '#4ce1b6',
    '#70bbfd',
    '#f6da6e',
    '#ff9f43',
    '#ff4861',
    '#8e7dff',
    '#26c6da',
    '#ef5350',
    '#66bb6a',
    '#ab47bc',
    '#ffa726',
    '#78909c',
  ];
  const paymentMethodColorMap = {
    Cash: '#4ce1b6',
    'Credit Card': '#ff4861',
    'Debit Card': '#70bbfd',
    'Bank Transfer': '#8e7dff',
    UPI: '#f6da6e',
    'Digital Wallet': '#26c6da',
    Cheque: '#ff9f43',
    Other: '#ab47bc',
    '—': '#78909c',
  };
  const paymentMethodChartData = (statistics?.payment_method_breakdown || [])
    .map((entry, idx) => {
      const name = String(entry?.method || entry?.name || entry?.payment_method || '').trim();
      return {
        name,
        value: Number(entry?.value) || 0,
        color: paymentMethodColorMap[name] || paymentMethodPalette[idx % paymentMethodPalette.length],
      };
    })
    .filter((item) => item.name && item.value > 0)
    .sort((a, b) => b.value - a.value);
  const totalCollection = paymentMethodChartData.reduce((sum, item) => sum + Number(item.value || 0), 0);

  const formatCurrency = (val) =>
    `₹${Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const { totalRooms, bookedRooms, occupancyPercentage } = computeOccupancyMetrics(
    statistics,
    startDate,
    endDate,
  );
  const displayStatistics = statistics || {};

  const handleDownloadPerformancePdf = useCallback(async () => {
    if (
      !statistics ||
      loading ||
      exportInProgressRef.current ||
      !reportPageOneRef.current ||
      !reportPageTwoRef.current
    ) return;

    exportInProgressRef.current = true;
    document.body.classList.add('dashboard-pdf-exporting');
    setPdfExporting(true);
    try {
      const safeHotelName = String(hotel?.name || 'hotel')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      const filename = `performance-dashboard-${safeHotelName}-${startDate.format('YYYY-MM-DD')}-${endDate.format('YYYY-MM-DD')}.pdf`;
      const rangeLabel = `${startDate.format('DD MMM YYYY')} to ${endDate.format('DD MMM YYYY')}`;

      await new Promise((resolve) => setTimeout(resolve, 120));
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (document.fonts?.ready) {
        await document.fonts.ready;
      }
      const logoDataUrl = await loadRoomnexaLogoDataUrl();
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const headerHeight = 28;
      const footerHeight = 13;
      const drawPdfHeader = (pageNumber) => {
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pageWidth, headerHeight, 'F');
        if (logoDataUrl) {
          pdf.addImage(logoDataUrl, 'PNG', (pageWidth - 36) / 2, 3.4, 36, 10);
        } else {
          pdf.setTextColor(30, 41, 59);
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(18);
          pdf.text('RoomNexa', pageWidth / 2, 8.5, { align: 'center' });
        }
        pdf.setTextColor(30, 41, 59);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.text('Performance Dashboard Report', pageWidth / 2, 18.6, { align: 'center' });
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7);
        pdf.setTextColor(100, 116, 139);
        pdf.text(
          `${hotel?.name || 'Selected hotel'} | ${rangeLabel} | Generated ${moment().format('DD MMM YYYY, hh:mm A')} | Page ${pageNumber}`,
          pageWidth / 2,
          23.6,
          { align: 'center' }
        );
        pdf.setDrawColor(226, 232, 240);
        pdf.line(margin, headerHeight - 2, pageWidth - margin, headerHeight - 2);
      };
      const drawPdfFooter = (pageNumber) => {
        const footerTop = pageHeight - footerHeight;
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, footerTop, pageWidth, footerHeight, 'F');
        pdf.setDrawColor(226, 232, 240);
        pdf.line(margin, footerTop, pageWidth - margin, footerTop);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(7.5);
        pdf.setTextColor(30, 41, 59);
        pdf.text('Powered by RoomNexa', margin, footerTop + 5.2);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(6.8);
        pdf.setTextColor(100, 116, 139);
        pdf.text(
          'Professional hotel performance intelligence for smarter operations.',
          margin,
          footerTop + 9.2
        );
        pdf.text(`Page ${pageNumber}`, pageWidth - margin, footerTop + 7.4, { align: 'right' });
      };
      const imageTop = headerHeight + margin;
      const printableHeight = pageHeight - imageTop - footerHeight - margin;
      const maxImageWidth = pageWidth - margin * 2;
      let pageIndex = 1;

      const captureElement = (element) => html2canvas(element, {
        backgroundColor: '#ffffff',
        scale: Math.min(2, window.devicePixelRatio || 1),
        useCORS: true,
        allowTaint: true,
        logging: false,
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight,
        onclone: (_documentClone, clonedElement) => {
          clonedElement.style.backgroundColor = '#ffffff';
        },
      });

      const addCanvasToPdf = (canvas, { fitSinglePage = false, forceNewPage = false } = {}) => {
        if (forceNewPage) {
          pdf.addPage();
          pageIndex += 1;
        }
        const imageData = canvas.toDataURL('image/png');
        if (fitSinglePage) {
          const widthByHeight = (canvas.width * printableHeight) / canvas.height;
          const imageWidth = Math.min(maxImageWidth, widthByHeight);
          const imageHeight = (canvas.height * imageWidth) / canvas.width;
          const imageLeft = (pageWidth - imageWidth) / 2;
          drawPdfHeader(pageIndex);
          pdf.addImage(imageData, 'PNG', imageLeft, imageTop, imageWidth, imageHeight);
          drawPdfFooter(pageIndex);
          return;
        }

        const imageWidth = maxImageWidth;
        const imageHeight = (canvas.height * imageWidth) / canvas.width;
        const imageLeft = (pageWidth - imageWidth) / 2;
        let renderedHeight = 0;
        while (renderedHeight < imageHeight) {
          drawPdfHeader(pageIndex);
          pdf.addImage(imageData, 'PNG', imageLeft, imageTop - renderedHeight, imageWidth, imageHeight);
          drawPdfFooter(pageIndex);
          renderedHeight += printableHeight;
          if (renderedHeight < imageHeight) {
            pdf.addPage();
            pageIndex += 1;
          }
        }
      };

      const pageOneCanvas = await captureElement(reportPageOneRef.current);
      const pageTwoCanvas = await captureElement(reportPageTwoRef.current);
      addCanvasToPdf(pageOneCanvas, { fitSinglePage: true });
      addCanvasToPdf(pageTwoCanvas, { forceNewPage: true });

      const formatReportDate = (dateValue) => moment(dateValue).format('DD MMM YYYY');
      const formatPdfAmount = (value) =>
        Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const formatPaymentBreakdown = (total, methods = []) => {
        const validMethods = (methods || []).filter((item) => Number(item?.value || 0) > 0);
        const totalLabel = formatPdfAmount(total || 0);
        if (!validMethods.length) return totalLabel;
        const detail = validMethods
          .map((item) => `${item.method || item.name || '-'} - ${formatPdfAmount(item.value)}`)
          .join('\n');
        return `${totalLabel}\n${detail}`;
      };
      const hotelTotalRooms = getHotelTotalRooms(statistics);
      const reportTableRows = (statistics?.daily_stats || []).map((stat) => {
        const roomTotal = Number(stat.total_hotel_rooms ?? stat.total ?? hotelTotalRooms ?? 0);
        const roomsBookedValue = Number(stat.rooms_booked ?? stat.booked_rooms ?? 0);
        const roomPct = roomTotal > 0 ? ((roomsBookedValue / roomTotal) * 100).toFixed(1) : '0.0';
        return [
          formatReportDate(stat.date),
          Number(stat.bookings || 0).toString(),
          `${roomsBookedValue}/${roomTotal} (${roomPct}%)`,
          formatPdfAmount(stat.expense),
          formatPdfAmount(stat.hotel_due),
          formatPaymentBreakdown(stat.revenue, stat.hotel_sale_payment_methods),
        ];
      });

      if (reportTableRows.length) {
        pdf.addPage();
        pageIndex += 1;
        drawPdfHeader(pageIndex);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(12);
        pdf.setTextColor(30, 41, 59);
        pdf.text('Daily Performance Details', margin, imageTop);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7.5);
        pdf.setTextColor(100, 116, 139);
        pdf.text(
          'Date-wise summary of bookings, room pickup, expenses, hotel due, and hotel sales.',
          margin,
          imageTop + 5
        );
        drawPdfFooter(pageIndex);

        autoTable(pdf, {
          startY: imageTop + 10,
          head: [[
            'Date',
            'Booking',
            'Booked Room / Total Room',
            'Hotel Expense',
            'Hotel Due',
            'Hotel Sale',
          ]],
          body: reportTableRows,
          margin: { top: imageTop, right: margin, bottom: footerHeight + 7, left: margin },
          tableWidth: 'auto',
          styles: {
            font: 'helvetica',
            fontSize: 6.4,
            cellPadding: 1.4,
            overflow: 'linebreak',
            valign: 'top',
            lineColor: [226, 232, 240],
            lineWidth: 0.1,
            textColor: [51, 65, 85],
          },
          headStyles: {
            fillColor: [30, 41, 59],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            halign: 'left',
          },
          alternateRowStyles: {
            fillColor: [248, 250, 252],
          },
          columnStyles: {
            0: { cellWidth: 22 },
            1: { cellWidth: 16, halign: 'center' },
            2: { cellWidth: 25 },
            3: { cellWidth: 24 },
            4: { cellWidth: 26 },
            5: { cellWidth: 38 },
            6: { cellWidth: 38 },
          },
          didParseCell: (data) => {
            if (data.section === 'body' && data.column.index >= 3) {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fontSize = 7.4;
              data.cell.styles.textColor = [15, 23, 42];
              data.cell.styles.cellPadding = 1.6;
            }
          },
          didDrawPage: () => {
            const currentPage = pdf.internal.getCurrentPageInfo().pageNumber;
            pdf.setFillColor(255, 255, 255);
            pdf.rect(0, 0, pageWidth, headerHeight, 'F');
            if (logoDataUrl) {
              pdf.addImage(logoDataUrl, 'PNG', (pageWidth - 36) / 2, 3.4, 36, 10);
            }
            pdf.setTextColor(30, 41, 59);
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(10);
            pdf.text('Performance Dashboard Report', pageWidth / 2, 18.6, { align: 'center' });
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(7);
            pdf.setTextColor(100, 116, 139);
            pdf.text(
              `${hotel?.name || 'Selected hotel'} | ${rangeLabel} | Generated ${moment().format('DD MMM YYYY, hh:mm A')} | Page ${currentPage}`,
              pageWidth / 2,
              23.6,
              { align: 'center' }
            );
            pdf.setDrawColor(226, 232, 240);
            pdf.line(margin, headerHeight - 2, pageWidth - margin, headerHeight - 2);
            drawPdfFooter(currentPage);
          },
        });
      }
      pdf.save(filename);
    } finally {
      document.body.classList.remove('dashboard-pdf-exporting');
      setPdfExporting(false);
      exportInProgressRef.current = false;
    }
  }, [
    endDate,
    hotel?.name,
    loading,
    startDate,
    statistics,
  ]);

  if (!hotel) {
    return (
      <Card
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 3,
          textAlign: 'center',
          border: `1px dashed ${muiTheme.palette.divider}`,
        }}
      >
        <Typography variant="h6" fontWeight={700} gutterBottom>
          No hotel selected
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: 'auto', lineHeight: 1.6 }}>
          Choose a property from the hotel menu in the header to load the same performance views as the landing
          dashboard.
        </Typography>
      </Card>
    );
  }

  const metricGrid = getInsightsMetricGridProps();
  const chartPrimaryGrid = getInsightsChartPrimaryGridProps();
  const chartSecondaryGrid = getInsightsChartSecondaryGridProps();
  const gridSpacing = getInsightsGridSpacing();

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <DashboardReportHeader
        title="Performance dashboard insights"
        subtitle="Bookings, revenue, occupancy and restaurant analytics in one view."
        startDate={startDate}
        endDate={endDate}
        onDateRangeChange={handleDateRangeChange}
        onRefresh={fetchData}
        onPdfDownload={handleDownloadPerformancePdf}
        loading={loading}
        pdfExporting={pdfExporting}
        pdfDisabled={!statistics}
        themeMode={currentTheme}
        accentColor={themeColors.booking}
      />

        {error && !loading && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            <Typography variant="subtitle2" fontWeight={700}>
              {error.title}
            </Typography>
            <Typography variant="body2">{error.message}</Typography>
          </Alert>
        )}

        {!error && (statistics || loading) && (
          <>
          <Box
            ref={reportPageOneRef}
            data-pdf-report-section
            sx={{
              p: { xs: 0.5, sm: 0 },
              backgroundColor: currentTheme === 'light' ? '#f2f4f7' : 'transparent',
            }}
          >
            <Grid container spacing={gridSpacing} sx={{ mb: gridSpacing }}>
              <Grid item {...metricGrid}>
                <DashboardMetricCard
                  variant="performance"
                  loading={loading}
                  title="Total Revenue"
                  value={displayStatistics.total_revenue || 0}
                  unit="revenue"
                  data={financeStatsData.map((stat) => ({ name: stat.name, value: stat.revenue }))}
                  color={themeColors.revenue}
                  icon={<MoneyIcon sx={{ color: 'inherit' }} />}
                  theme={currentTheme}
                  formatValue={formatCurrency}
                />
              </Grid>
              <Grid item {...metricGrid}>
                <DashboardMetricCard
                  variant="performance"
                  loading={loading}
                  title="Hotel expenses"
                  value={displayStatistics.total_expense ?? displayStatistics.total_expenses ?? 0}
                  unit="hotel purchases"
                  data={financeStatsData.map((stat) => ({ name: stat.name, value: stat.expense }))}
                  color={themeColors.expense}
                  icon={<TrendingDownIcon sx={{ color: 'inherit' }} />}
                  theme={currentTheme}
                  formatValue={formatCurrency}
                />
              </Grid>
              <Grid item {...metricGrid}>
                <DashboardMetricCard
                  variant="performance"
                  loading={loading}
                  title="Hotel Due"
                  value={displayStatistics.total_hotel_due || displayStatistics.hotel_due_summary?.due || 0}
                  unit="checkout due"
                  data={financeStatsData.map((stat) => ({ name: stat.name, value: stat.hotelDue }))}
                  color={themeColors.hotelDue}
                  icon={<TrendingDownIcon sx={{ color: 'inherit' }} />}
                  theme={currentTheme}
                  formatValue={formatCurrency}
                />
              </Grid>
              <Grid item {...metricGrid}>
                <DashboardMetricCard
                  variant="performance"
                  loading={loading}
                  title="Total Profit"
                  value={displayStatistics.total_profit || 0}
                  unit="profit"
                  data={financeStatsData.map((stat) => ({ name: stat.name, value: stat.profit }))}
                  color={themeColors.profit}
                  icon={<TrendingUpIcon sx={{ color: 'inherit' }} />}
                  theme={currentTheme}
                  formatValue={formatCurrency}
                />
              </Grid>
            </Grid>

            <Grid container spacing={gridSpacing} sx={{ mb: gridSpacing }}>
              <Grid item {...metricGrid}>
                <DashboardMetricCard
                  variant="performance"
                  loading={loading}
                  title="Total Bookings / Cancelled Bookings"
                  value={`${displayStatistics.total_bookings || 0}/${displayStatistics.cancelled_bookings || 0}`}
                  unit="bookings"
                  data={bookingStatsData.map((stat) => {
                    const total = Number(stat.bookings || 0);
                    const cancelled = Number(stat.cancelled || 0);
                    return {
                      name: stat.name,
                      value: total > 0 ? Number(((cancelled / total) * 100).toFixed(1)) : 0,
                    };
                  })}
                  color={themeColors.cancelled}
                  icon={<CancelIcon sx={{ color: 'inherit' }} />}
                  theme={currentTheme}
                  formatValue={(val) => val.toString()}
                />
              </Grid>
              <Grid item {...metricGrid}>
                <DashboardMetricCard
                  variant="performance"
                  loading={loading}
                  title="Booked Room / Total Room"
                  value={`${bookedRooms}/${totalRooms}`}
                  unit="rooms"
                  data={inventoryStatsData.map((stat) => ({
                    name: stat.name,
                    value: Number(stat.rooms_booked || stat.booked || 0),
                  }))}
                  color={themeColors.active}
                  icon={<CheckCircleIcon sx={{ color: 'inherit' }} />}
                  theme={currentTheme}
                />
              </Grid>
              <Grid item {...metricGrid}>
                <DashboardMetricCard
                  variant="performance"
                  loading={loading}
                  title="Occupancy Percentage"
                  value={occupancyPercentage}
                  unit="occupancy"
                  data={inventoryStatsData.map((stat) => {
                    const hotelTotalRooms = getHotelTotalRooms(statistics);
                    const total = Number(stat.total ?? hotelTotalRooms ?? 0);
                    const booked = Number(stat.rooms_booked || 0);
                    return {
                      name: stat.name,
                      value: total > 0 ? Number(((booked / total) * 100).toFixed(1)) : 0,
                    };
                  })}
                  color={themeColors.inventory}
                  icon={<InventoryIcon sx={{ color: 'inherit' }} />}
                  theme={currentTheme}
                  formatValue={(val) => `${Number(val || 0).toFixed(1)}%`}
                />
              </Grid>
              <Grid item {...metricGrid}>
                <DashboardMetricCard
                  variant="performance"
                  loading={loading}
                  title="Total Customers"
                  value={displayStatistics.total_customers || 0}
                  unit="customers"
                  data={[]}
                  color={themeColors.customer}
                  icon={<PeopleIcon sx={{ color: 'inherit' }} />}
                  theme={currentTheme}
                  formatValue={(val) => val.toString()}
                />
              </Grid>
            </Grid>

            <Grid container spacing={gridSpacing} sx={{ mb: gridSpacing }}>
              <Grid item {...chartPrimaryGrid}>
                <DashboardChartCard
                  variant="performance"
                  loading={loading}
                  eyebrow="FINANCE STATISTICS"
                  subtitle="Revenue with hotel purchases and checkout due"
                  data={financeStatsData}
                  chartType="bar"
                  dataKeys={[
                    { dataKey: 'revenue', name: 'Total Revenue', color: themeColors.revenue },
                    { dataKey: 'expense', name: 'Hotel expenses', color: themeColors.expense, stackId: 'expenses' },
                    { dataKey: 'hotelDue', name: 'Hotel Due', color: themeColors.hotelDue, stackId: 'expenses' },
                  ]}
                  theme={currentTheme}
                  height={300}
                  formatValue={formatCurrency}
                  barRadius={[0, 0, 0, 0]}
                />
              </Grid>
              <Grid item {...chartSecondaryGrid}>
                <DashboardChartCard
                  variant="performance"
                  loading={loading}
                  eyebrow="PAYMENT METHODS"
                  subtitle={`Selected range collection: ${formatCurrency(totalCollection)}`}
                  chartType="pie"
                  pieData={paymentMethodChartData}
                  pieAsBars
                  pieInnerRadius={70}
                  pieShowValueInLabel
                  theme={currentTheme}
                  height={300}
                  formatValue={formatCurrency}
                />
              </Grid>
            </Grid>

            <Grid container spacing={gridSpacing} sx={{ mb: gridSpacing }}>
              <Grid item {...chartPrimaryGrid}>
                <DashboardChartCard
                  variant="performance"
                  loading={loading}
                  eyebrow="BOOKING STATISTICS"
                  subtitle="Bookings split by status over time"
                  data={bookingStatsData}
                  chartType="bar"
                  dataKeys={[
                    { dataKey: 'active', name: 'Active Bookings', color: unifiedChartColors.green, stackId: 'bookingStatus' },
                    { dataKey: 'cancelled', name: 'Cancelled Bookings', color: unifiedChartColors.red, stackId: 'bookingStatus' },
                    { dataKey: 'booking', name: 'Booking', color: unifiedChartColors.yellow, stackId: 'bookingStatus' },
                  ]}
                  theme={currentTheme}
                  height={300}
                  formatValue={(val) => val.toString()}
                  showBarTotalTooltip
                  barTotalLabel="Total Bookings"
                  barRadius={[0, 0, 0, 0]}
                />
              </Grid>
              <Grid item {...chartSecondaryGrid}>
                <DashboardChartCard
                  variant="performance"
                  loading={loading}
                  eyebrow="BOOKING SOURCES"
                  subtitle={`Total bookings: ${totalSourceBookings}`}
                  chartType="pie"
                  pieData={bookingSourceChartData}
                  pieAsBars
                  pieInnerRadius={70}
                  pieShowValueInLabel
                  theme={currentTheme}
                  height={300}
                  formatValue={(val) => Number(val || 0).toString()}
                />
              </Grid>
            </Grid>

            </Box>

            <Box
              ref={reportPageTwoRef}
              data-pdf-report-section
              sx={{
                p: { xs: 0.5, sm: 0 },
                backgroundColor: currentTheme === 'light' ? '#f2f4f7' : 'transparent',
              }}
            >
            <Grid container spacing={gridSpacing} sx={{ mb: gridSpacing }}>
              <Grid item xs={12}>
                <DashboardChartCard
                  variant="performance"
                  loading={loading}
                  eyebrow="INVENTORY TRENDS"
                  subtitle="Direct, OTA and overbooking inventory by date"
                  data={inventoryStatsData}
                  chartType="bar"
                  dataKeys={[
                    { dataKey: 'direct_booked', name: 'Direct Bookings', color: themeColors.directBooked, stackId: 'inventory' },
                    { dataKey: 'online_booked', name: 'OTA Bookings', color: themeColors.onlineBooked, stackId: 'inventory' },
                    { dataKey: 'overbooked', name: 'Overbooking', color: themeColors.overbooked, stackId: 'inventory' },
                  ]}
                  theme={currentTheme}
                  height={340}
                  formatValue={(val) => val.toString()}
                  barRadius={[0, 0, 0, 0]}
                />
              </Grid>
            </Grid>
          </Box>
          </>
        )}

        {!loading && !error && !statistics && hotel && (
          <Typography color="text.secondary" variant="body2">
            No statistics returned for this range.
          </Typography>
        )}
    </Box>
  );
}
